import React from 'react';
import { Col, Form, Row, Select } from 'components';
import { FormModal } from 'components/common';
import { useClientTypeAddEditPricing } from 'feature/countryTabs/pricing/clientTypePricingRules/edit/useClientTypeAddEditPricing';
import { InputNumber } from 'antd';
import { preventNonNumericValue } from 'utils/helpers';
import { PercentageIcon } from 'components/common/icons';
import { ValidationErrors } from 'constants/validations';

const { Item } = Form;

export type CountryClientPricingRuleType = {
  countryId: string;
  clientTypePricingRulesId: string;
  clientTypeId: string;
  markup: number;
  minMargin: number;
  maxMargin: number;
};

export interface IEditPricing {
  modalState: boolean;
  onModalStateChange: () => void;
  dataToEdit?: Record<string, any>;
}

export const CountryClientTypeAddEditModal = React.memo((props: IEditPricing) => {
  const { onModalStateChange, modalState, dataToEdit } = props;
  const { onFinish, contextHolder, form, isLoading, clearFieldValidation, onCancel, clientTypeData } =
    useClientTypeAddEditPricing({
      onModalStateChange,
      modalState,
      dataToEdit,
    });
  return (
    <>
      {contextHolder}
      <FormModal
        form={form}
        title={`${dataToEdit ? 'Update' : 'Add'} Client Type Pricing Rules`}
        okText={dataToEdit ? 'Save' : 'Add'}
        open={modalState}
        isLoading={isLoading}
        onCancel={onCancel}
        onSubmit={onFinish}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Item
              label={'Client Type*'}
              name={'clientTypeId'}
              rules={[{ required: true, message: ValidationErrors.required('Client Type') }]}
            >
              <Select options={clientTypeData} size={'large'} />
            </Item>
          </Col>
          <Col span={12}>
            <Item
              label={'Margin'}
              name={'markup'}
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const num = parseFloat(value);
                    if (isNaN(num)) {
                      return Promise.reject(new Error('Margin must be a valid number.'));
                    }
                    if (num < 0) {
                      return Promise.reject(new Error('Margin must be a positive number.'));
                    }
                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                      return Promise.reject(new Error('Margin can have up to 2 decimal points.'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber<number>
                controls={false}
                size={'large'}
                onBeforeInput={(event: any) => preventNonNumericValue(event)}
                style={{ width: '100%' }}
                suffix={<PercentageIcon />}
              />
            </Item>
          </Col>

          <Col span={6}>
            <Item
              label={'Margin Range'}
              name={'minMargin'}
              rules={[
                ({ getFieldValue }) => ({
                  validator: async (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const num = parseFloat(value);
                    if (isNaN(num)) {
                      return Promise.reject(new Error('Min margin must be a valid number.'));
                    }
                    if (num < 0) {
                      return Promise.reject(new Error('Min margin must be a positive number.'));
                    }
                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                      return Promise.reject(new Error('Min margin can have up to 2 decimal points.'));
                    }
                    const maxMargin = getFieldValue('maxMargin');
                    if (maxMargin !== undefined && maxMargin !== null && num > maxMargin) {
                      return Promise.reject(new Error('Min margin cannot be greater than Max margin.'));
                    }
                    clearFieldValidation(form, 'maxMargin');
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber<number>
                controls={false}
                size={'large'}
                onBeforeInput={(event: any) => preventNonNumericValue(event)}
                style={{ width: '100%' }}
                suffix={<PercentageIcon />}
                placeholder={'Min'}
              />
            </Item>
          </Col>

          <Col span={6}>
            <Item
              label={<span style={{ display: 'none' }}></span>}
              name={'maxMargin'}
              rules={[
                ({ getFieldValue }) => ({
                  validator: async (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const num = parseFloat(value);
                    if (isNaN(num)) {
                      return Promise.reject(new Error('Max margin must be a valid number.'));
                    }
                    if (num < 0) {
                      return Promise.reject(new Error('Max margin must be a positive number.'));
                    }
                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                      return Promise.reject(new Error('Max margin can have up to 2 decimal points.'));
                    }

                    const minMargin = getFieldValue('minMargin');
                    if (minMargin !== undefined && num < minMargin) {
                      return Promise.reject(new Error('Max margin cannot be less than Min margin.'));
                    }
                    clearFieldValidation(form, 'minMargin');
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber<number>
                controls={false}
                size={'large'}
                onBeforeInput={(event: any) => preventNonNumericValue(event)}
                style={{ width: '100%' }}
                suffix={<PercentageIcon />}
                placeholder={'Max'}
              />
            </Item>
          </Col>
        </Row>
      </FormModal>
    </>
  );
});
